import {
    Invoice,
    PopoverDivider,
    PopoverItem,
    ProviderGuarantor,
    ProviderPaymentPlan,
    ToastContext
} from '@enter/core';
import { mdiChevronDown } from '@mdi/js';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DialogContext, ErrorDialog } from 'services';
import { parseError } from 'services/dialog/util';
import { RootStore } from 'stores';
import * as S from './guarantor-action-button.styles';
import { GuarantorDocumentDialog } from './guarantor-document-dialog';
import { showRecordDiscountDialog } from './show-record-discount-dialog';
import { PaymentPlanDialog } from './payment-plan-dialog';

export interface IGuarantorActionButtonProps {
    className?: string;
    claimId: string;
    guarantor: ProviderGuarantor;
    invoice?: Invoice;
}

export const GuarantorActionButton: React.FC<IGuarantorActionButtonProps> = inject('rootStore')(
    observer(
        (props: IGuarantorActionButtonProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { className, claimId, guarantor, invoice } = props;

            const dialogContext = useContext(DialogContext);
            const toastContext = useContext(ToastContext);
            const [paymentPlan, setPaymentPlan] = useState<ProviderPaymentPlan | undefined>(undefined);
            const [paymentActionTitle, setPaymentActionTitle] = useState<string>('');

            const updatePlans = useCallback(async (): Promise<void> => {
                if (invoice && invoice.id) {
                    const plans = await claimStore.findPaymentPlansByInvoiceId(invoice.id);
                    if (plans && plans.length) {
                        setPaymentPlan(plans[0]);
                        setPaymentActionTitle('Disable payment plan');
                    } else {
                        setPaymentPlan(undefined);
                        setPaymentActionTitle('Add payment plan');
                    }
                }
            }, [claimStore, invoice, setPaymentPlan]);

            useEffect(() => {
                updatePlans();
            }, [updatePlans]);

            const renderButton = (): React.ReactElement => {
                return (
                    <S.ActionButton className={className}>
                        Manage Billing
                        <S.ActionButtonIcon path={mdiChevronDown} />
                    </S.ActionButton>
                );
            };
            const viewBill = async (): Promise<void> => {
                try {
                    if (guarantor && guarantor.id) {
                        const tempUrl = await claimStore.getGuarantorViewBillUrl(guarantor.id);
                        if (tempUrl && tempUrl.url) {
                            window.open(tempUrl.url, '_blank');
                            return;
                        }
                    }
                } catch (err) {
                    dialogContext.showError(err);
                }
                toastContext.notify?.('Billing document is currently unavailable.', 'right');
            };
            const payBill = async (): Promise<void> => {
                if (!guarantor?.id) {
                    return;
                }

                try {
                    const shortUrl = await claimStore.getGuarantorCheckoutUrl(guarantor.id);
                    if (shortUrl && shortUrl.url) {
                        window.open(shortUrl.url, '_blank');
                    }
                } catch (err) {
                    const parsedError = await parseError(err);
                    if (parsedError.payload.code === 'err.billing_not_active_for_invoice') {
                        dialogContext.showDialog({
                            props: {
                                onClose: handleCloseDialog,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Billing is not active for this invoice yet',
                            },
                            Component: (
                                <ErrorDialog
                                    onClose={handleCloseDialog}
                                    payload={{
                                        message: 'If you feel this is in error, please reach out to support.',
                                        hideTrace: true,
                                    }}
                                />
                            ),
                        });
                    } else {
                        dialogContext.showError(err);
                    }
                }
            };

            const handleCloseDialog = () => {
                dialogContext.hideDialog();
            };

            const showGuarantorDocumentDialog = async (): Promise<void> => {
                if (invoice) {
                    dialogContext.showDialog({
                        props: {
                            onClose: handleCloseDialog,
                            disableBackdropClick: true,
                            disableEscapeKeyDown: true,
                            renderTitle: 'View all bills',
                        },
                        Component: (
                            <GuarantorDocumentDialog
                                onClose={handleCloseDialog}
                                claimId={claimId}
                                guarantor={guarantor}
                            />
                        ),
                    });
                }
            };
            const showInvoiceItemDialog = (): void => {
                if (invoice) {
                    showRecordDiscountDialog({ dialogContext, toastContext, claimStore, claimId, invoice });
                }
            };

            const showPaymentDialog = async (): Promise<void> => {
                if (invoice) {
                    dialogContext.showDialog({
                        props: {
                            onClose: handleCloseDialog,
                            disableBackdropClick: true,
                            disableEscapeKeyDown: true,
                            renderTitle: paymentActionTitle,
                        },
                        Component: (
                            <PaymentPlanDialog
                                onClose={handleCloseDialog}
                                invoice={invoice}
                                paymentPlan={paymentPlan}
                            />
                        ),
                    });
                }
            };

            return (
                <>
                    <S.StyledPopover align="right" renderButton={renderButton}>
                        <PopoverItem onClick={payBill}>Pay Bill</PopoverItem>
                        <PopoverItem onClick={viewBill}>View Bill</PopoverItem>
                        <PopoverItem onClick={showGuarantorDocumentDialog}>View all bills</PopoverItem>
                        {invoice && <PopoverDivider />}
                        {
                            // NB: temporarily remove Payment Plan options pending API changes
                            false && invoice && (
                                <PopoverItem onClick={showPaymentDialog}>{paymentActionTitle}</PopoverItem>
                            )
                        }
                        {invoice && <PopoverItem onClick={showInvoiceItemDialog}>Set Discount Amount</PopoverItem>}
                    </S.StyledPopover>
                </>
            );
        },
    ),
);
